import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Lubion — subcutaneous progesterone, the luteal injection a patient gives
 * herself every day for ten weeks, and the first walk here that runs past
 * the cycle into a pregnancy.
 *
 * ### Two presentations, two walks
 *
 * The UK leaflet exists twice: one for the prefilled syringe (a needle
 * staked on, nothing to draw up) and one for the vial, where she draws the
 * whole vial up through a large needle, swaps to a fine one, and pushes the
 * air out to a drop. Same medicine, same site, same pinch, same angle — and
 * four steps that exist for one box and not the other. So two protocols
 * share everything but their steps, as Pergoveris does, and the device
 * question offers both.
 *
 * ### One language on the label
 *
 * Lubion is a UK and Irish product (emc; the Irish leaflet is the same
 * text). No French Lubion exists. Its sister Prolutex is on the Swiss and
 * southern-European markets, and the Swiss French patient information was
 * read for vocabulary — but it describes a different procedure: a 45° angle,
 * an aspiration check with "if blood appears, start again with a new vial",
 * the abdomen only, and a massage. Those are contested between the two
 * labels and the fiche lists them as such. The walk follows the Lubion
 * leaflet; **the French copy is the author's reading of the English**,
 * sentence for sentence, and is flagged for a reviewer who reads both.
 * Prolutex is not claimed as a brand until an EEA Prolutex leaflet is read.
 *
 * ### What the leaflet says that no other walk here says
 *
 * "Insert the needle all the way into the skin" and "the skin and the
 * needle form a right angle" — stated as a right angle rather than 90°,
 * which is why the figure test learned that phrase. And the stop list is
 * four lines long: anaphylaxis, hyperstimulation, depression, jaundice —
 * the last two are progesterone's own, and route to the clinic.
 *
 * Restated from `content/data/medications/progesterone-support.json` (the
 * `Lubion` entry), whose text was a disclaimer with no steps until it was
 * written from the two leaflets.
 */

const LUBION_BRANDS = [
  { name: "Lubion", markets: ["GB", "IE", "EU"] },
] as const satisfies readonly ProtocolBrand[];

/** The leaflet's own precondition: practice, where, how. */
const FIRST_INJECTION: ProtocolWarning = {
  id: "lubion.first-injection",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "First injection - Inject yourself only after your doctor or healthcare professional has had you practise subcutaneous injections and shown you where to inject and how to give your medicine.",
    fr: "Première injection - Ne vous injectez vous-même qu'après que votre médecin ou votre professionnel de santé vous a fait pratiquer des injections sous-cutanées et vous a montré où injecter et comment administrer votre médicament.",
  },
  sourceSegment:
    "⚠ First injection - Inject yourself only after your doctor or healthcare professional has had you practise subcutaneous injections and shown you where to inject and how to give your medicine.",
};

/** The leaflet's rule, no hour, and the one that says to tell the doctor. */
const MISSED_DOSE: ProtocolWarning = {
  id: "lubion.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Take the dose as soon as you remember and then carry on as before. Do not take a double dose to make up for a forgotten dose. Tell your doctor what you have done.",
    fr: "Dose oubliée - Prenez la dose dès que vous vous en souvenez, puis continuez comme avant. Ne prenez pas de dose double pour compenser la dose que vous avez oublié de prendre. Dites à votre médecin ce que vous avez fait.",
  },
  sourceSegment:
    "⚠ Missed dose - Take the dose as soon as you remember and then carry on as before. Do not take a double dose to make up for a forgotten dose. Tell your doctor what you have done.",
};

/** The leaflet's anaphylaxis line, routed to emergency as for every medication. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "lubion.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - A severe allergic reaction, with difficulty breathing, swelling of the face and throat or a severe rash, needs emergency services straight away.",
    fr: "Aide urgente - Une réaction allergique grave, avec une difficulté à respirer, un gonflement du visage et de la gorge ou une éruption cutanée sévère, nécessite d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - A severe allergic reaction, with difficulty breathing, swelling of the face and throat or a severe rash, needs emergency services straight away.",
};

/**
 * The rest of the leaflet's "stop and seek immediate medical help" list —
 * hyperstimulation, depression, jaundice — to the clinic now. Depression and
 * jaundice are progesterone's own; no other walk here carries them.
 */
const STOP_AND_SEEK_HELP: ProtocolWarning = {
  id: "lubion.stop-and-seek-help",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Stop and seek help - Stop the injections and seek immediate medical help if you have lower stomach pain with thirst, sickness, passing little concentrated urine and weight gain, if you feel depressed, or if your skin or the whites of your eyes turn yellow.",
    fr: "Arrêter et consulter - Arrêtez les injections et consultez immédiatement un médecin en cas de douleur dans le bas du ventre avec soif, nausées, émission de petites quantités d'urines concentrées et prise de poids, si vous vous sentez déprimée, ou si votre peau ou le blanc de vos yeux jaunissent.",
  },
  sourceSegment:
    "⚠ Stop and seek help - Stop the injections and seek immediate medical help if you have lower stomach pain with thirst, sickness, passing little concentrated urine and weight gain, if you feel depressed, or if your skin or the whites of your eyes turn yellow.",
};

/** "e.g. tummy or front of thigh", shown by the doctor — so she is asked. */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Clean the site - Inject where your doctor or healthcare professional showed you: here, on your abdomen. Open the alcohol wipe, carefully clean the area of skin to be injected and allow it to dry.",
      fr: "Désinfecter le site - Injectez là où votre médecin ou votre professionnel de santé vous l'a montré : ici, sur le ventre. Ouvrez le tampon d'alcool, nettoyez soigneusement la zone de peau à injecter et laissez sécher.",
    },
  },
  {
    id: "thigh",
    label: { en: "Front of thigh", fr: "Devant de la cuisse" },
    copy: {
      en: "Clean the site - Inject where your doctor or healthcare professional showed you: here, on the front of your thigh. Open the alcohol wipe, carefully clean the area of skin to be injected and allow it to dry.",
      fr: "Désinfecter le site - Injectez là où votre médecin ou votre professionnel de santé vous l'a montré : ici, sur le devant de la cuisse. Ouvrez le tampon d'alcool, nettoyez soigneusement la zone de peau à injecter et laissez sécher.",
    },
  },
];

/** The injection itself is the same sentence on both leaflets. */
const INJECT_COPY = {
  en: "Inject - Hold the syringe in one hand and, with the other, gently pinch the skin at the injection site between your thumb and index finger. Using a dart-like motion, insert the needle all the way into the skin so that the skin and the needle form a right angle; do not inject directly into a vein. Push the plunger gently, in a slow and steady motion, until all the solution is injected beneath the skin. Then release the skin and pull the needle straight out.",
  fr: "Injecter - Tenez la seringue d'une main et, de l'autre, pincez doucement la peau au niveau du site d'injection entre le pouce et l'index. D'un geste sec, comme une fléchette, enfoncez l'aiguille entièrement dans la peau de façon que la peau et l'aiguille forment un angle droit ; n'injectez pas directement dans une veine. Appuyez doucement sur le piston, d'un mouvement lent et régulier, jusqu'à ce que toute la solution soit injectée sous la peau. Relâchez ensuite la peau et retirez l'aiguille tout droit.",
};

const SYRINGE_STEPS: ProtocolStep[] = [
  {
    id: "lubion.syringe.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash your hands thoroughly and dry them on a clean towel; it is important to keep everything as clean as possible. Choose a clean area and lay out one prefilled syringe of {brand}, two alcohol wipes and a sharps container; the wipes and the container are not supplied with your medicine. One injection a day, usually until the twelfth week of a confirmed pregnancy, exactly as your doctor told you.",
      fr: "Préparer - Lavez-vous soigneusement les mains et séchez-les avec une serviette propre ; il est important que tout reste aussi propre que possible. Choisissez un endroit propre et disposez une seringue préremplie de {brand}, deux tampons d'alcool et un collecteur ; les tampons et le collecteur ne sont pas fournis avec votre médicament. Une injection par jour, en général jusqu'à la douzième semaine d'une grossesse confirmée, exactement comme votre médecin vous l'a dit.",
    },
    warnings: [FIRST_INJECTION, MISSED_DOSE],
  },
  {
    id: "lubion.syringe.inspect",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the pack - Do not use the product if the packaging is open or damaged. Make sure that the prefilled syringe contains the solution and that the expiry date has not passed. Do not use it if you notice particles in the solution or if the solution is not clear.",
      fr: "Vérifier l'emballage - N'utilisez pas le produit si l'emballage est ouvert ou endommagé. Assurez-vous que la seringue préremplie contient bien la solution et que la date de péremption n'est pas dépassée. Ne l'utilisez pas si vous remarquez des particules dans la solution ou si la solution n'est pas limpide.",
    },
  },
  {
    id: "lubion.syringe.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: SITES[0]!.copy,
  },
  {
    id: "lubion.syringe.ready-the-syringe",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Ready the syringe - Remove the cap; once it is off, do not let the needle touch anything, so that it stays clean and sterile. Hold the syringe with the needle pointing upwards and slowly press the plunger until all the air is out of the syringe and at least one drop of solution comes out of the tip of the needle.",
      fr: "Préparer la seringue - Retirez le capuchon ; une fois retiré, ne laissez l'aiguille toucher aucune surface, pour qu'elle reste propre et stérile. Tenez la seringue aiguille vers le haut et appuyez lentement sur le piston jusqu'à ce que tout l'air soit sorti de la seringue et qu'au moins une goutte de solution apparaisse à la pointe de l'aiguille.",
    },
  },
  {
    id: "lubion.syringe.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: INJECT_COPY,
    warnings: [ALLERGIC_REACTION],
  },
  {
    id: "lubion.syringe.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Wipe the skin at the injection site with an alcohol swab using a circular motion. Put the used syringe into your sharps container; each prefilled syringe is for single use only. Do not stop using {brand} without speaking to your doctor or pharmacist first: stopping abruptly may cause increased anxiety, moodiness and a higher risk of seizures.",
      fr: "Après l'injection - Essuyez la peau au point d'injection avec un tampon d'alcool, en décrivant des mouvements circulaires. Jetez la seringue utilisée dans votre collecteur ; chaque seringue préremplie est à usage unique. N'arrêtez pas {brand} sans en parler d'abord à votre médecin ou à votre pharmacien : un arrêt brutal peut provoquer une anxiété accrue, des sautes d'humeur et un risque plus élevé de convulsions.",
    },
    warnings: [STOP_AND_SEEK_HELP],
  },
];

const VIAL_STEPS: ProtocolStep[] = [
  {
    id: "lubion.vial.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash your hands thoroughly and dry them on a clean towel; it is important to keep everything as clean as possible. Choose a clean area and lay out one vial of {brand}, a syringe, a large needle for drawing up, a small fine needle for the injection under the skin, two alcohol wipes and a sharps container; only the vial is supplied with your medicine. Each vial is used once, and the solution is used immediately after opening the vial; it must not be stored in the syringe. One injection a day, usually until the twelfth week of a confirmed pregnancy, exactly as your doctor told you.",
      fr: "Préparer - Lavez-vous soigneusement les mains et séchez-les avec une serviette propre ; il est important que tout reste aussi propre que possible. Choisissez un endroit propre et disposez un flacon de {brand}, une seringue, une grande aiguille pour prélever, une petite aiguille fine pour l'injection sous la peau, deux tampons d'alcool et un collecteur ; seul le flacon est fourni avec votre médicament. Chaque flacon ne sert qu'une fois, et la solution s'utilise immédiatement après l'ouverture du flacon ; elle ne doit pas être conservée dans la seringue. Une injection par jour, en général jusqu'à la douzième semaine d'une grossesse confirmée, exactement comme votre médecin vous l'a dit.",
    },
    warnings: [FIRST_INJECTION, MISSED_DOSE],
  },
  {
    id: "lubion.vial.inspect",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the pack - The vial, the syringe and the needles all have protective covers: check that all the covers are on firmly, and do not use them if they are not on properly or are damaged. Make sure that the expiry date on the vial has not passed. Do not use it if you notice particles in the solution or if the solution is not clear.",
      fr: "Vérifier l'emballage - Le flacon, la seringue et les aiguilles ont tous des protections : vérifiez que toutes les protections sont bien en place, et ne les utilisez pas si elles sont mal mises ou endommagées. Assurez-vous que la date de péremption du flacon n'est pas dépassée. Ne l'utilisez pas si vous remarquez des particules dans la solution ou si la solution n'est pas limpide.",
    },
  },
  {
    // The whole vial is the dose, and `{dose}` says so where the vial is
    // emptied into the syringe.
    id: "lubion.vial.draw-up-the-dose",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Draw up the dose - Remove the plastic cap from the top of the vial by gently pushing it upwards, wipe the rubber top with an alcohol wipe and allow it to dry. Attach the large needle to the syringe and remove its cover. Push the large needle through the rubber middle of the vial top, then, with the needle still inserted, turn the vial upside down. Make sure the needle tip is underneath the level of the liquid and gently pull the plunger to draw all the solution into the syringe: one vial is your dose{dose}. Then pull the large needle out of the vial.",
      fr: "Prélever la dose - Retirez le capuchon en plastique du haut du flacon en le poussant doucement vers le haut, essuyez le bouchon en caoutchouc avec un tampon d'alcool et laissez sécher. Fixez la grande aiguille sur la seringue et retirez sa protection. Enfoncez la grande aiguille au milieu du bouchon en caoutchouc du flacon, puis, l'aiguille toujours en place, retournez le flacon tête en bas. Assurez-vous que la pointe de l'aiguille est sous le niveau du liquide et tirez doucement sur le piston pour aspirer toute la solution dans la seringue : un flacon est votre dose{dose}. Retirez ensuite la grande aiguille du flacon.",
    },
  },
  {
    id: "lubion.vial.change-the-needle",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Change the needle - Put the cover back on the large needle and gently take it off the syringe. Take the small fine needle from its package, keeping its cover on, attach it to the syringe and then remove the cover.",
      fr: "Changer l'aiguille - Remettez la protection sur la grande aiguille et retirez-la doucement de la seringue. Sortez la petite aiguille fine de son emballage en gardant sa protection, fixez-la sur la seringue, puis retirez la protection.",
    },
  },
  {
    id: "lubion.vial.check-for-a-droplet",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Remove the air bubbles - Hold the syringe straight up with the needle pointing to the ceiling, draw back slightly on the plunger and tap the syringe so that any air bubbles rise to the top. Slowly press the plunger until all the air is out of the syringe and at least one drop of solution comes out of the tip of the needle.",
      fr: "Chasser les bulles d'air - Tenez la seringue bien droite, aiguille vers le plafond, tirez légèrement sur le piston et tapotez la seringue pour que les bulles d'air remontent. Appuyez lentement sur le piston jusqu'à ce que tout l'air soit sorti de la seringue et qu'au moins une goutte de solution apparaisse à la pointe de l'aiguille.",
    },
  },
  {
    id: "lubion.vial.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: SITES[0]!.copy,
  },
  {
    id: "lubion.vial.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: INJECT_COPY,
    warnings: [ALLERGIC_REACTION],
  },
  {
    id: "lubion.vial.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Wipe the skin at the injection site with an alcohol swab using a circular motion. Put all the needles, the empty vial and the syringe into your sharps container; any unused solution must also be thrown away. Do not stop using {brand} without speaking to your doctor or pharmacist first: stopping abruptly may cause increased anxiety, moodiness and a higher risk of seizures.",
      fr: "Après l'injection - Essuyez la peau au point d'injection avec un tampon d'alcool, en décrivant des mouvements circulaires. Jetez toutes les aiguilles, le flacon vide et la seringue dans votre collecteur ; toute solution non utilisée doit aussi être jetée. N'arrêtez pas {brand} sans en parler d'abord à votre médecin ou à votre pharmacien : un arrêt brutal peut provoquer une anxiété accrue, des sautes d'humeur et un risque plus élevé de convulsions.",
    },
    warnings: [STOP_AND_SEEK_HELP],
  },
];

const SHARED = {
  medicationSlug: "lubion",
  brands: LUBION_BRANDS,
  // "Progesterone" alone is not claimed: it is also progesterone in oil, an
  // intramuscular injection with its own needle and its own walk to come.
  aliases: [
    "Subcutaneous progesterone",
    "Aqueous progesterone",
    "Progestérone sous-cutanée",
  ],
  indication: "luteal-support",
  // 25 mg, one vial or one syringe, once a day. A record in another unit is
  // asked about, never converted.
  dose: { unit: "mg", strengths: [25] },
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom:
    "content/data/medications/progesterone-support.json#progesterone-subcutaneous",
  sites: SITES,
} as const satisfies Partial<InjectionProtocol>;

export const LUBION_SYRINGE_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  steps: SYRINGE_STEPS,
};

export const LUBION_VIAL_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "vial",
  // A ready solution drawn up with her own syringe, like Lupron: "flacon +
  // seringue" on her form, not a powder to reconstitute.
  prescriptionForms: ["injection_solution"],
  steps: VIAL_STEPS,
};

export const LUBION_PROTOCOLS: readonly InjectionProtocol[] = [
  LUBION_SYRINGE_PROTOCOL,
  LUBION_VIAL_PROTOCOL,
];
